/**
 * useIPO -- IPO Tracker & Corporate Actions store (Zustand).
 */
import { create } from 'zustand'
import {
  getUpcomingIPOs,
  getRecentIPOs,
  getIPOPerformance,
  getCorporateSplits,
  getCorporateDividends,
} from '@/lib/api'
import type {
  UpcomingIPO,
  RecentIPO,
  IPOPerformanceResponse,
  SplitsResponse,
  CorporateDividend,
} from '@/lib/types'

interface IPOState {
  // Upcoming IPOs
  upcoming: UpcomingIPO[]
  upcomingLoading: boolean

  // Recent IPOs
  recent: RecentIPO[]
  recentLoading: boolean
  /** Honest timestamp of the cached list being displayed. */
  recentAsOf: string | null
  /** True when upstream was slow and this is the last stored copy. */
  recentStale: boolean
  recentNote: string | null

  // Performance
  performance: IPOPerformanceResponse | null
  performanceLoading: boolean

  // Splits
  splits: SplitsResponse | null
  splitsLoading: boolean

  // Dividends
  dividends: CorporateDividend[]
  dividendsLoading: boolean

  error: string | null

  fetchUpcoming: () => Promise<void>
  fetchRecent: (days?: number) => Promise<void>
  fetchPerformance: () => Promise<void>
  fetchSplits: (days?: number) => Promise<void>
  fetchDividends: (days?: number) => Promise<void>
}

export const useIPO = create<IPOState>()((set) => ({
  upcoming: [],
  upcomingLoading: false,
  recent: [],
  recentLoading: false,
  recentAsOf: null,
  recentStale: false,
  recentNote: null,
  performance: null,
  performanceLoading: false,
  splits: null,
  splitsLoading: false,
  dividends: [],
  dividendsLoading: false,
  error: null,

  fetchUpcoming: async () => {
    set({ upcomingLoading: true, error: null })
    try {
      const upcoming = await getUpcomingIPOs()
      set({ upcoming, upcomingLoading: false })
    } catch (e) {
      set({ error: String(e), upcomingLoading: false })
    }
  },

  fetchRecent: async (days = 30) => {
    set({ recentLoading: true, error: null })
    try {
      const res = await getRecentIPOs(days)
      set({
        recent: res.results ?? [],
        recentAsOf: res.as_of ?? null,
        recentStale: res.stale ?? false,
        recentNote: res.note ?? null,
        recentLoading: false,
      })
    } catch (e) {
      set({ error: String(e), recentLoading: false })
    }
  },

  fetchPerformance: async () => {
    set({ performanceLoading: true, error: null })
    try {
      const performance = await getIPOPerformance()
      set({ performance, performanceLoading: false })
    } catch (e) {
      set({ error: String(e), performanceLoading: false })
    }
  },

  fetchSplits: async (days = 90) => {
    set({ splitsLoading: true, error: null })
    try {
      const splits = await getCorporateSplits(days)
      set({ splits, splitsLoading: false })
    } catch (e) {
      set({ error: String(e), splitsLoading: false })
    }
  },

  fetchDividends: async (days = 30) => {
    set({ dividendsLoading: true, error: null })
    try {
      const dividends = await getCorporateDividends(days)
      set({ dividends, dividendsLoading: false })
    } catch (e) {
      set({ error: String(e), dividendsLoading: false })
    }
  },
}))
